import React, {createContext, useContext, useState} from 'react'
import {
  drawLayout,
  getCellphoneAssignment,
  getScheduleAssignment,
} from '../../services/assigment.services'
import {
  CellphoneAssignModel,
  LayoutModelDto,
  ScheduleAssignmentFilter,
  ScheduleAssignModel,
  SearchAssigmentModel,
} from '../../model/assigment.model'
import {CREATION_CELLPHONES_SERVICES} from '../../services'
import {CREATION_CLEANING_ACTIVITIES_SERVICES} from '../../services/creation-cleaning-activities.service'
import {
  CellphoneAssignmentFilter,
  CellphoneColumnModel,
  CellphoneDto,
} from '../../model/creationCellphones.model'
import {
  CleaningActivitiesColumnModel,
  CleaningActivitiesDto,
} from '../../model/creationCleaningActivities.model'

/* =========================
   TYPES
========================= */

type AssigmentContextType = {
  loading: boolean
  loadingCelulares: boolean
  loadingActividades: boolean
  loadingProgramaciones: boolean
  layout: {}[]
  celularesDisponibles: CellphoneAssignModel[]
  programacionesDisponibles: ScheduleAssignModel[]
  actividadesDisponibles: CleaningActivitiesColumnModel[]
  selectedPhones: CellphoneAssignModel[]
  selectedSchedules: ScheduleAssignModel[]
  tipoData: string
  setTipoData: any
  setLayout: any
  getLayout: (filters: SearchAssigmentModel) => Promise<LayoutModelDto[]>
  getCelularesDisponibles: (
    filters: CellphoneAssignmentFilter,
  ) => Promise<CellphoneAssignModel[] | undefined>
  getProgramacionesDisponibles: (
    filters: ScheduleAssignmentFilter,
  ) => Promise<ScheduleAssignModel[] | undefined>
  getActividadesDisponibles: (
    filters: CleaningActivitiesDto,
  ) => Promise<CleaningActivitiesColumnModel[] | undefined>
  clearLayout: () => void
  clearCelularesDisponibles: () => void
  clearActividadesDisponibles: () => void
  clearSelectedPhones: () => void
  setSelectedPhonesFn: (phones: CellphoneAssignModel[]) => void
  setSelectedSchedulesFn: (schedules: ScheduleAssignModel[]) => void
  clearSelectedSchedules: () => void
}

/* =========================
   LOGICA INTERNA (PRIMERO)
========================= */

const useAssigmentProvider = (): AssigmentContextType => {
  const [loading, setLoading] = useState(false)
  const [loadingCelulares, setLoadingCelulares] = useState(false)
  const [loadingActividades, setLoadingActividades] = useState(false)
  const [loadingProgramaciones, setLoadingProgramaciones] = useState(false)
  const [programacionesDisponibles, setProgramacionesDisponibles] = useState<ScheduleAssignModel[]>(
    [],
  )
  const [layout, setLayout] = useState<LayoutModelDto[]>([])
  const [tipoData, setTipoData] = useState<string>('')
  const [celularesDisponibles, setCelularesDisponibles] = useState<CellphoneAssignModel[]>([])
  const [selectedPhones, setSelectedPhones] = useState<any[]>([])
  const [selectedSchedules, setSelectedSchedules] = useState<any[]>([])
  const [actividadesDisponibles, setActividadesDisponibles] = useState<
    CleaningActivitiesColumnModel[]
  >([])

  const getLayout = async (filters: SearchAssigmentModel) => {
    try {
      setLoading(true)
      const {data: response} = await drawLayout(filters)
      setTipoData(filters.TipoData)
      setLayout(response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching layout:', error)
    } finally {
      setLoading(false)
    }
  }

  const getCelularesDisponibles = async (filters: CellphoneAssignmentFilter) => {
    try {
      setLoadingCelulares(true)
      const {data: response} = await getCellphoneAssignment(filters)
      if (!response.error) {
        setCelularesDisponibles(response.data)
        return response.data
      }
    } catch (error) {
      console.error('Error fetching celulares:', error)
    } finally {
      setLoadingCelulares(false)
    }
  }

  const getProgramacionesDisponibles = async (filters: ScheduleAssignmentFilter) => {
    try {
      setLoadingProgramaciones(true)
      const {data: response} = await getScheduleAssignment(filters)
      if (!response.error) {
        setProgramacionesDisponibles(response.data)
        return response.data
      }
    } catch (error) {
      console.error('Error fetching programaciones:', error)
    } finally {
      setLoadingProgramaciones(false)
    }
  }

  const getActividadesDisponibles = async (filters: CleaningActivitiesDto) => {
    try {
      setLoadingActividades(true)
      const {data: response} =
        await CREATION_CLEANING_ACTIVITIES_SERVICES.getCleaningActivitiesList(filters)
      if (!response.error) {
        setActividadesDisponibles(response.data)
        return response.data
      }
    } catch (error) {
      console.error('Error fetching actividades:', error)
    } finally {
      setLoadingActividades(false)
    }
  }

  const setSelectedPhonesFn = (phones: CellphoneColumnModel[]) => {
    setSelectedPhones(phones)
  }

  const setSelectedSchedulesFn = (schedules: ScheduleAssignModel[]) => {
    setSelectedSchedules(schedules)
  }

  const clearSelectedPhones = () => setSelectedPhones([])
  const clearSelectedSchedules = () => setSelectedSchedules([])
  const clearLayout = () => setLayout([])
  const clearCelularesDisponibles = () => setCelularesDisponibles([])
  const clearActividadesDisponibles = () => setActividadesDisponibles([])

  return {
    loading,
    tipoData,
    layout,
    celularesDisponibles,
    actividadesDisponibles,
    programacionesDisponibles,
    loadingCelulares,
    loadingActividades,
    loadingProgramaciones,
    selectedPhones,
    selectedSchedules,
    setTipoData,
    setLayout,
    getLayout,
    getCelularesDisponibles,
    getProgramacionesDisponibles,
    getActividadesDisponibles,
    clearLayout,
    clearCelularesDisponibles,
    clearActividadesDisponibles,
    setSelectedPhonesFn,
    setSelectedSchedulesFn,
    clearSelectedSchedules,
    clearSelectedPhones,
  }
}

/* =========================
   CONTEXT
========================= */

const AssigmentContext = createContext<AssigmentContextType | null>(null)

/* =========================
   PROVIDER
========================= */

export const AssigmentProvider = ({children}: {children: React.ReactNode}) => {
  const value = useAssigmentProvider()
  return <AssigmentContext.Provider value={value}>{children}</AssigmentContext.Provider>
}

/* =========================
   HOOK PUBLICO
========================= */

export const useAssigment = () => {
  const context = useContext(AssigmentContext)
  if (!context) {
    throw new Error('useAssigment must be used within an AssigmentProvider')
  }
  return context
}
